import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import SEOHead from "../components/shared/SEOHead";
import Breadcrumbs, { generateBreadcrumbSchema } from "../components/shared/Breadcrumbs";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Package, Lock, Thermometer, Clock } from "lucide-react";
import { createServiceSchema, createFAQSchema } from "../seo/schemas";

const StoragePage = () => {
  const features = [
    { icon: Lock, title: "24/7 Security", desc: "CCTV monitoring and secure access" },
    { icon: Thermometer, title: "Climate Controlled", desc: "Protected from temperature extremes" },
    { icon: Clock, title: "Flexible Terms", desc: "From 1 week to long-term storage" },
    { icon: Package, title: "All Sizes", desc: "From a few boxes to entire house contents" }
  ];

  const breadcrumbs = [
    { label: "Services", href: "/services" },
    { label: "Storage Solutions", href: "/services/storage" },
  ];

  const pageFaqs = [
    { q: "How much does storage cost in Cardiff?", a: "Our storage starts from £50 per week for a small unit. Pricing depends on the space you need — contact us for a tailored quote." },
    { q: "Can you collect and deliver items to storage?", a: "Yes, we offer a full collection and delivery service. We can pick up your items, store them securely, and deliver them when you need them." },
    { q: "Is the storage facility secure?", a: "Absolutely. Our facility has 24/7 CCTV monitoring, secure access controls, and individual unit alarms. Your belongings are fully protected." },
    { q: "How long can I store my items?", a: "We offer flexible terms from just one week to long-term storage. There are no long-term commitments — you can access or remove your items at any time." },
  ];

  const jsonLd = [
    createServiceSchema("Storage Solutions Cardiff", "Secure, climate-controlled storage in Cardiff. 24/7 security, flexible terms, collection and delivery available.", "/services/storage"),
    createFAQSchema(pageFaqs),
    generateBreadcrumbSchema(breadcrumbs),
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Storage Solutions Cardiff | Secure & Flexible | Hitech Transit"
        description="Secure, climate-controlled storage in Cardiff from £50/week. 24/7 CCTV, flexible terms, collection & delivery available. Ideal for house moves or business storage."
        canonical="/services/storage"
        jsonLd={jsonLd}
      />
      <Header />
      
      <main className="pt-20">
        <Breadcrumbs items={breadcrumbs} />
        {/* Hero */}
        <section className="relative py-20 lg:py-32">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/80ea756f-7ea3-4803-b754-18b59043e07a/images/a879bd00e5dab9522432a50f08568dc566f8ca7d78c427daf5c1df7c7bca5622.png)`
            }}
          >
            <div className="absolute inset-0 bg-[#0F172A]/70" />
          </div>
          
          <div className="container-max px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-4 block">
                Storage Solutions
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-['Outfit'] font-bold text-white tracking-tight mb-6">
                Secure Storage for Your Belongings
              </h1>
              <p className="text-slate-200 text-lg mb-8">
                Whether you're between homes, decluttering, or need extra space, our secure storage 
                facilities keep your belongings safe and accessible.
              </p>
              <Link to="/instant-quote" className="btn-primary inline-flex items-center gap-2" data-testid="storage-quote-btn">
                Get Storage Quote
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="section-padding bg-[#F8F9FA]">
          <div className="container-max">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <div key={feature.title || feature.name || `feature-${index}`} className="text-center">
                  <div className="w-14 h-14 bg-[#FF5A1F]/10 mx-auto flex items-center justify-center mb-4">
                    <feature.icon className="w-7 h-7 text-[#FF5A1F]" />
                  </div>
                  <h3 className="text-lg font-['Outfit'] font-bold text-[#0F172A] mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="section-padding">
          <div className="container-max">
            <div className="text-center mb-12">
              <span className="label-text">Storage Rates</span>
              <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-[#0F172A] mt-2">
                Simple, Transparent Pricing
              </h2>
            </div>
            
            <div className="max-w-3xl mx-auto">
              <div className="bg-[#0F172A] p-8 text-center mb-8">
                <div className="text-6xl font-['Outfit'] font-black text-[#FF5A1F] mb-2">
                  £50
                </div>
                <div className="text-white text-lg">per week</div>
                <div className="text-slate-400 text-sm mt-2">Minimum 1 week storage</div>
              </div>
              
              <div className="space-y-4">
                {[
                  "Collection from your property included",
                  "Delivery to your new address included",
                  "Full contents insurance",
                  "Clean, dry, secure units",
                  "No hidden fees or charges",
                  "Flexible access arrangements"
                ].map((item, index) => (
                  <div key={item.title || item.name || `item-${index}`} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#FF5A1F] flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="text-center mt-12">
                <Link to="/instant-quote" className="btn-primary inline-flex items-center gap-2" data-testid="storage-get-quote-btn">
                  Get Storage Quote
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="section-padding bg-white">
          <div className="container-max">
            <h2 className="text-3xl font-['Outfit'] font-bold text-[#0F172A] mb-8">
              Frequently Asked Questions About Storage
            </h2>
            <div className="max-w-3xl space-y-6">
              {pageFaqs.map((faq, index) => (
                <div key={`faq-${index}`} className="border-b border-slate-200 pb-6">
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{faq.q}</h3>
                  <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default StoragePage;
